import React from "react";
import {
  Box,
  ListItem,
  Typography,
  IconButton,
  Tooltip,
  useTheme,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import { getNotificationDetails } from "core/notifications";
import { getNotificationIcon } from "core/notifications/utils/getNotificationIcon";
import { usePublicUserList } from "hooks/user/usePublicUserList";

interface NotificationItemProps {
  notification: any;
  onMarkAsRead?: (e: React.MouseEvent, id?: string) => void;
  onNavigate?: (path: string) => void;
  formatDate: (date: Date | string) => string;
  showMarkAsRead?: boolean;
  compact?: boolean;
}

export const NotificationItem: React.FC<NotificationItemProps> = ({
  notification,
  onMarkAsRead,
  onNavigate,
  formatDate,
  showMarkAsRead = true,
  compact = false,
}) => {
  const theme = useTheme();
  const { data: users = [] } = usePublicUserList();
  
  const displayParams = getNotificationDetails(notification);
  
  const getActorName = () => {
    if (!notification.actorId) return null;
    const actorUser = users.find(user => user.id === notification.actorId);
    if (actorUser) {
      return `${actorUser.firstName} ${actorUser.lastName}`;
    }
    return null;
  };

  const getSeverityColor = (severity: "info" | "success" | "warning" | "error") => {
    switch (severity) {
      case "error":
        return theme.palette.error.main;
      case "warning":
        return theme.palette.warning.main;
      case "success":
        return theme.palette.success.main;
      case "info":
      default:
        return theme.palette.primary.main;
    }
  };

  const actorName = getActorName();
  const severityColor = getSeverityColor(displayParams.severity);
  const IconComponent = getNotificationIcon(notification.type);

  return (
    <ListItem
      onClick={() => {
        if (displayParams.clickDestination && onNavigate) {
          onNavigate(displayParams.clickDestination);
        }
      }}
      sx={{
        display: "flex",
        alignItems: "flex-start",
        width: "calc(100% - 16px)",
        maxWidth: "100%",
        p: compact ? 1 : 2,
        cursor: displayParams.isClickable ? "pointer" : "default",
        borderRadius: 1,
        mx: compact ? 0 : 1,
        mb: compact ? 0.5 : 1,
        bgcolor: notification.read ? "transparent" : "action.hover",
        border: `1px solid ${notification.read ? "transparent" : severityColor}20`,
        transition: "all 0.2s ease-in-out",
        overflow: "hidden",
        "&:hover": displayParams.isClickable
          ? {
              bgcolor: "action.selected",
              transform: "translateY(-1px)",
              boxShadow: theme.shadows[1],
            }
          : {},
        "&:last-child": {
          mb: 0,
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          mr: compact ? 1 : 1.5,
          mt: 0.5,
          flexShrink: 0,
        }}
      >
        <IconComponent
          sx={{
            fontSize: compact ? 18 : 22,
            color: severityColor,
          }}
        />
      </Box>

      <Box sx={{ flex: 1, minWidth: 0, mr: 1 }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: 0.5 }}>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: notification.read ? 400 : 600,
              color: "text.primary",
              fontSize: compact ? "0.75rem" : "0.875rem",
              lineHeight: 1.3,
              flex: 1,
              mr: 1,
              wordWrap: "break-word",
              overflowWrap: "break-word",
              hyphens: "auto",
            }}
          >
            {displayParams.title}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              fontSize: compact ? "0.65rem" : "0.75rem",
              flexShrink: 0,
              whiteSpace: "nowrap",
            }}
          >
            {formatDate(notification.createdAt || "")}
          </Typography>
        </Box>

        {actorName && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: displayParams.description ? 0.5 : 0 }}>
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                fontSize: compact ? "0.65rem" : "0.75rem",
                fontWeight: 500,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: "150px",
              }}
            >
              by {actorName}
            </Typography>
          </Box>
        )}

        {displayParams.description && (
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              fontSize: compact ? "0.7rem" : "0.8125rem",
              lineHeight: 1.4,
              display: "-webkit-box",
              WebkitLineClamp: compact ? 1 : 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              textOverflow: "ellipsis",
              wordBreak: "break-word",
              overflowWrap: "break-word",
              hyphens: "auto",
              width: "100%",
            }}
          >
            {displayParams.description}
          </Typography>
        )}
      </Box>

      {showMarkAsRead && onMarkAsRead && (
        <Tooltip title="Mark as read" placement="top">
          <IconButton
            onClick={(e) => onMarkAsRead(e, notification.id)}
            size="small"
            sx={{
              color: "text.secondary",
              opacity: 0.7,
              flexShrink: 0,
              "&:hover": {
                bgcolor: "action.hover",
                opacity: 1,
              },
            }}
          >
            <CheckIcon sx={{ fontSize: compact ? 16 : 18 }} />
          </IconButton>
        </Tooltip>
      )}
    </ListItem>
  );
}; 